import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { fromInvoices } from '../../../../store/rootReducer';
import { isTestCloudFeature, OrganizationFeature, TestOpsPlanInterval } from '../../../../models';
import { getPlanId } from '../utils';
import { useAppDispatch } from '../../../../store';

const useStyles = makeStyles(({
  root: {},
  container: {
    width: '100%',
  },
  namePriceContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    position: 'relative',
  },
  name: {
    fontSize: 14,
    fontWeight: 500,
    color: '#233145',
    width: '70%',
    marginBottom: 0,
  },
  sumPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#22283c',
    top: 0,
    right: 0,
    position: 'absolute',
  },
  circleProgress: {
    top: 12,
    right: 0,
    position: 'absolute',
  },
  unitPriceBillingCycleContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    width: '100%',
  },
  unitPrice: {
    fontSize: 12,
    fontWeight: 500,
    color: '#727993',
  },
  billingCycle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#4d5369',
    bottom: 0,
    right: 0,
    position: 'absolute',
  },
  line: {
    border: 'none',
    borderTop: '1px dashed #d6dae4',
  },
  discountPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#008060',
    position: 'absolute',
    top: 0,
    right: 0,
  },
}));

interface CreditItemProps {
  currentSubscription?: any,
  organizationFeature: OrganizationFeature,
  accountId: number,
  planName: string,
  packageName: string,
}

const CreditItem = (props: CreditItemProps) => {
  const {
    currentSubscription,
    accountId,
    organizationFeature,
    planName,
    packageName,
  } = props;

  const classes = useStyles();
  const intl = useIntl();
  const preview = useSelector(fromInvoices
    .selectPreviewTerminatedPreviewByFeature(organizationFeature), shallowEqual);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const quota = getQuota();
    if (quota === 0) return;

    const getPreviewInvoice = async () => {
      dispatch(fromInvoices.doPreviewTerminatedMonthlyInvoice({
        organizationId: accountId,
        planId: getPlanId(
          organizationFeature,
          TestOpsPlanInterval.YEAR,
        ),
        number: getQuota(),
        newQuotaNumber: 0,
        // eslint-disable-next-line max-len
        ...(currentSubscription && {
          subscriptionId: currentSubscription.id,
          recurlySubscriptionUuid: currentSubscription.recurlySubscription?.recurlySubscriptionUuid,
        }),
      }));
    };

    getPreviewInvoice().catch(() => {
    });
  }, []);

  const loading = useSelector(fromInvoices
    .selectLoadingTerminatedPreviewByFeature(organizationFeature));

  const getQuota = () => (isTestCloudFeature(currentSubscription.feature)
    ? currentSubscription.concurrentSessions
    : currentSubscription.quota);

  const getQuotaForView = () => `${getQuota()} `;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <div className={classes.namePriceContainer}>
          <p className={classes.name}>
            <FormattedMessage id={planName} />
          </p>
          {
            loading
              ? <CircularProgress className={classes.circleProgress} size={20} />
              : (
                <p className={classes.discountPrice}>
                  - $
                  {intl.formatNumber(Math.abs(preview?.creditAmount || 0))}
                </p>
              )
          }
        </div>
        <div className={classes.unitPriceBillingCycleContainer}>
          <p className={classes.unitPrice}>
            {getQuotaForView()}
            <FormattedMessage id="subscriptions.testops_platform.monthly" />
            {' '}
            <FormattedMessage id={packageName} />
            {' '}
          </p>
        </div>
      </div>
      <div className={classes.line} />
    </div>
  );
};

export default CreditItem;
